import { defineComponent, ref, reactive, computed, watchEffect, onMounted, nextTick, onBeforeUnmount, createElementBlock, openBlock, normalizeStyle, unref, createBlock, createCommentVNode, createElementVNode, Teleport, withKeys, normalizeClass, createTextVNode, toDisplayString, createVNode, Fragment, renderList, withCtx, renderSlot } from 'vue';
import { useElementSize, useBreakpoints } from './basler-components.es363.js';
import _sfc_main$1 from './basler-components.es79.js';
/* empty css                       */
import _sfc_main$2 from './basler-components.es70.js';
import { BREAKPOINTS } from './basler-components.es385.js';
import { useDebounceFn } from './basler-components.es386.js';

const _hoisted_1 = { class: "article-toc-layout-panel__inner" };
const _hoisted_2 = { class: "article-toc-layout-panel__header" };
const _hoisted_3 = ["aria-expanded"];
const _hoisted_4 = {
  key: 0,
  class: "article-toc-layout-panel__trigger-btn-text"
};
const _hoisted_5 = { class: "visually-hidden" };
const _hoisted_6 = {
  key: 1,
  class: "article-toc-layout-panel__trigger-btn-text"
};
const _hoisted_7 = ["disabled", "aria-label"];
const _hoisted_8 = ["disabled", "aria-label"];
const _hoisted_9 = {
  id: "article-toc-layout-panel__main-01",
  class: "article-toc-layout-panel__main"
};
const _hoisted_10 = { class: "nav-secondary__main-outer" };
const _hoisted_11 = { class: "nav-secondary__main-inner" };
const _hoisted_12 = { class: "article-toc-layout-panel__list" };
const _hoisted_13 = ["href"];
const _hoisted_14 = {
  key: 1,
  class: "article-toc-layout-panel"
};
const _hoisted_15 = ["aria-label"];
const _hoisted_16 = {
  class: "article-toc-layout-panel__nav",
  "aria-labelledby": "article-toc-layout-panel__title-01"
};
const _hoisted_17 = {
  id: "article-toc-layout-panel__title-01",
  class: "article-toc-layout-panel__title"
};
const _hoisted_18 = { class: "article-toc-layout-panel__list" };
const _hoisted_19 = ["href"];
const _hoisted_20 = {
  ref: "mainContent",
  class: "article-toc-layout__main-content",
  tabindex: "-1"
};
const maxGenerationAttempts = 5;
const _sfc_main = /* @__PURE__ */ defineComponent({
  __name: "ArticleTocLayout",
  props: {
    mobileTitle: { default: "Table of contents" },
    asideAriaLabel: { default: "Table of contents" },
    navHeading: { default: "Content on this page" },
    openTocBtnActionLabel: { default: "Open " },
    prevBtnLabel: { default: "Previous section" },
    nextBtnLabel: { default: "Next section" },
    tocSkipBtnText: { default: "Skip to main content" }
  },
  setup(__props) {
    const articleTocLayoutRef = ref(null);
    const boxRef = ref(null);
    const mainContentRef = ref(null);
    const isOpen = ref(false);
    const tocItems = ref([]);
    const latestActiveSectionId = ref("");
    const intersectingSections = reactive(/* @__PURE__ */ new Set());
    const sectionGoToAction = ref("prev");
    const { height: boxHeight } = useElementSize(boxRef, void 0, { box: "border-box" });
    const breakpoints = useBreakpoints(BREAKPOINTS);
    const isMounted = ref(false);
    const isDesktop = computed(() => {
      if (!isMounted.value) {
        return false;
      }
      return breakpoints.greaterOrEqual("lg").value;
    });
    const activeSectionId = computed(() => {
      const activeItem = tocItems.value.find((item) => item.isActive);
      return activeItem?.id || latestActiveSectionId.value || "";
    });
    const activeHeadline = computed(() => {
      if (!activeSectionId.value) {
        return "";
      }
      const activeItem = tocItems.value.find((item) => item.id === activeSectionId.value);
      const displayIndex = currentTocIndex.value >= 0 ? currentTocIndex.value + 1 : 0;
      return `${displayIndex}. ${activeItem?.text || ""}`;
    });
    const currentTocIndex = computed(() => tocItems.value.findIndex((item) => item.id === activeSectionId.value));
    const hasPrevious = computed(() => currentTocIndex.value > 0);
    const hasNext = computed(() => {
      const hasActiveItems = tocItems.value.some((item) => item.isActive);
      return hasActiveItems ? currentTocIndex.value < tocItems.value.length - 1 : tocItems.value.length > 0;
    });
    const scrollToSection = async (item) => {
      tocItems.value = tocItems.value.map((tocItem) => ({
        ...tocItem,
        isActive: tocItem.id === item.id
      }));
      await nextTick();
      item.section?.scrollIntoView({
        behavior: "smooth",
        block: "start"
      });
    };
    const goToPrevious = () => {
      if (!hasPrevious.value) return;
      sectionGoToAction.value = "prev";
      isOpen.value = false;
      const previousItem = tocItems.value[currentTocIndex.value - 1];
      if (previousItem) scrollToSection(previousItem);
    };
    const goToNext = () => {
      if (!hasNext.value) return;
      sectionGoToAction.value = "next";
      isOpen.value = false;
      const nextIndex = (currentTocIndex.value + 1) % tocItems.value.length;
      const nextItem = tocItems.value[nextIndex];
      if (nextItem?.section) {
        scrollToSection(nextItem);
      }
    };
    const tocGenerationAttempts = ref(0);
    const generateTocItems = async () => {
      if (!articleTocLayoutRef.value) {
        console.warn("ArticleTocLayout: Container ref not available");
        return;
      }
      try {
        const items = [];
        const contentSections = articleTocLayoutRef.value.querySelectorAll(".content-section");
        if (contentSections.length === 0 && tocGenerationAttempts.value < maxGenerationAttempts) {
          tocGenerationAttempts.value++;
          console.warn(
            `ArticleTocLayout: No content sections found, retrying... (attempt ${tocGenerationAttempts.value}/${maxGenerationAttempts})`
          );
          const delay = Math.min(50 * Math.pow(2, tocGenerationAttempts.value - 1), 800);
          setTimeout(() => generateTocItems(), delay);
          return;
        }
        if (contentSections.length > 0) {
          tocGenerationAttempts.value = 0;
        }
        contentSections.forEach((section, sectionIndex) => {
          const contentSectionInner = section.querySelector(".content-section__inner");
          const firstChild = contentSectionInner?.firstElementChild;
          if (!firstChild) return;
          if (firstChild.classList.contains("text-block") || firstChild.classList.contains("text-media-block") || firstChild.classList.contains("headline-block")) {
            const h2Element = firstChild.querySelectorAll("h2");
            h2Element.forEach((h2, headlineIndex) => {
              if (h2Element) {
                const text = h2.textContent?.trim() || "";
                if (text) {
                  const id = h2.id || `section-toc-item-${sectionIndex}-${headlineIndex + 1}`;
                  if (!h2.id) h2.id = id;
                  items.push({
                    id,
                    text,
                    section,
                    headline: h2,
                    isActive: false
                  });
                }
              }
            });
          }
        });
        if (items.length > 0) {
          tocItems.value = items;
          await nextTick();
          setupIntersectionObserver();
        }
      } catch (error) {
        console.warn("Failed to generate TOC items:", error);
      }
    };
    const getIntersectingItem = (type) => {
      if (type === "prev") {
        return tocItems.value.find((item) => intersectingSections.has(item.id));
      } else {
        const intersectingItems = tocItems.value.filter((item) => intersectingSections.has(item.id));
        return intersectingItems[intersectingItems.length - 1];
      }
    };
    const updateActiveSection = () => {
      tocItems.value.forEach((item) => item.isActive = false);
      if (intersectingSections.size === 0) {
        const firstSectionId = tocItems.value.length > 0 ? tocItems.value[0].id : "";
        const latestActiveItem = tocItems.value.find((item) => item.id === latestActiveSectionId.value);
        const isLatestActiveFirstSection = latestActiveSectionId.value === firstSectionId;
        if (isLatestActiveFirstSection) {
          latestActiveSectionId.value = "";
          return;
        }
        if (latestActiveItem) latestActiveItem.isActive = true;
        return;
      }
      const firstIntersectingItem = getIntersectingItem(sectionGoToAction.value);
      if (firstIntersectingItem) {
        firstIntersectingItem.isActive = true;
      }
    };
    let intersectionObserver = null;
    const cleanupIntersectionObserver = () => {
      if (intersectionObserver !== null) {
        intersectionObserver.disconnect();
        intersectionObserver = null;
      }
    };
    const setupIntersectionObserver = () => {
      cleanupIntersectionObserver();
      const validItems = tocItems.value.filter(
        (item) => item.headline && item.headline instanceof HTMLHeadingElement && item.headline.isConnected
      );
      if (validItems.length === 0) {
        console.warn("No valid sections found for intersection observation");
        return;
      }
      intersectionObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const sectionId = entry.target.id;
            if (entry.isIntersecting) {
              intersectingSections.add(sectionId);
            } else {
              intersectingSections.delete(sectionId);
            }
          });
          updateActiveSection();
        },
        {
          rootMargin: "-20% 0px -60% 0px",
          threshold: [0, 0.5, 1]
        }
      );
      validItems.forEach((item) => {
        intersectionObserver?.observe(item.headline);
      });
    };
    let mutationObserver = null;
    const debouncedGenerateTocItems = useDebounceFn(() => {
      generateTocItems();
    }, 150);
    const setupMutationObserver = () => {
      if (!isMounted.value || !articleTocLayoutRef.value) return;
      mutationObserver = new MutationObserver((mutations) => {
        let shouldRegenerate = false;
        mutations.forEach((mutation) => {
          if (mutation.type === "childList") {
            mutation.addedNodes.forEach((node) => {
              if (node instanceof Element && (node.classList.contains("content-section") || node.querySelector(".content-section"))) {
                shouldRegenerate = true;
              }
            });
          }
        });
        if (shouldRegenerate) {
          tocGenerationAttempts.value = 0;
          debouncedGenerateTocItems();
        }
      });
      mutationObserver.observe(articleTocLayoutRef.value, {
        childList: true,
        subtree: true,
        attributeFilter: ["class"]
      });
    };
    const onMobileLinkClick = () => {
      isOpen.value = false;
    };
    const handleEscKey = (event) => {
      if (event.key === "Escape" && isOpen.value) {
        isOpen.value = false;
      }
    };
    const onSkipBtnClick = () => {
      if (mainContentRef.value) {
        mainContentRef.value.tabIndex = -1;
        mainContentRef.value.focus();
      }
    };
    watchEffect(() => {
      const currentActiveId = activeSectionId.value;
      if (currentActiveId && tocItems.value.some((item) => item.isActive)) {
        latestActiveSectionId.value = currentActiveId;
      }
    });
    onMounted(async () => {
      isMounted.value = true;
      await nextTick();
      setupMutationObserver();
      await nextTick();
      generateTocItems();
    });
    onBeforeUnmount(() => {
      intersectingSections.clear();
      tocItems.value = [];
      latestActiveSectionId.value = "";
      tocGenerationAttempts.value = 0;
      cleanupIntersectionObserver();
      if (mutationObserver !== null) {
        mutationObserver.disconnect();
        mutationObserver = null;
      }
    });
    return (_ctx, _cache) => {
      return openBlock(), createElementBlock("div", {
        ref_key: "articleTocLayoutRef",
        ref: articleTocLayoutRef,
        class: "article-toc-layout",
        style: normalizeStyle({ "--box-height": unref(boxHeight) + "px" })
      }, [
        tocItems.value.length && !isDesktop.value ? (openBlock(), createBlock(Teleport, {
          key: 0,
          to: "#page-header-secondary-bar",
          defer: ""
        }, [
          createElementVNode("div", {
            class: normalizeClass(["article-toc-layout-panel", { "is-open": isOpen.value }]),
            onKeydownPassive: withKeys(handleEscKey, ["esc"])
          }, [
            createElementVNode("div", _hoisted_1, [
              createElementVNode("div", _hoisted_2, [
                createElementVNode("button", {
                  type: "button",
                  class: "article-toc-layout-panel__trigger-btn",
                  "aria-expanded": isOpen.value ? "true" : void 0,
                  "aria-controls": "article-toc-layout-panel__main-01",
                  onClick: _cache[0] || (_cache[0] = ($event) => isOpen.value = !isOpen.value)
                }, [
                  !activeSectionId.value ? (openBlock(), createElementBlock("span", _hoisted_4, [
                    createElementVNode("span", _hoisted_5, toDisplayString(_ctx.openTocBtnActionLabel), 1),
                    createTextVNode(toDisplayString(_ctx.mobileTitle), 1)
                  ])) : (openBlock(), createElementBlock("span", _hoisted_6, toDisplayString(activeHeadline.value), 1))
                ], 8, _hoisted_3),
                createElementVNode("button", {
                  type: "button",
                  class: "article-toc-layout-panel__prev-btn",
                  disabled: !hasPrevious.value,
                  "aria-label": _ctx.prevBtnLabel,
                  onClick: goToPrevious
                }, [
                  createVNode(_sfc_main$1, { name: "arrow-down-bold" })
                ], 8, _hoisted_7),
                createElementVNode("button", {
                  type: "button",
                  class: "article-toc-layout-panel__next-btn",
                  disabled: !hasNext.value,
                  "aria-label": _ctx.nextBtnLabel,
                  onClick: goToNext
                }, [
                  createVNode(_sfc_main$1, { name: "arrow-down-bold" })
                ], 8, _hoisted_8)
              ]),
              createElementVNode("div", _hoisted_9, [
                createElementVNode("div", _hoisted_10, [
                  createElementVNode("div", _hoisted_11, [
                    createElementVNode("ol", _hoisted_12, [
                      (openBlock(true), createElementBlock(Fragment, null, renderList(tocItems.value, (item) => {
                        return openBlock(), createElementBlock("li", {
                          key: item.id,
                          class: normalizeClass({ "is-active": item.isActive })
                        }, [
                          createElementVNode("a", {
                            href: `#${item.id}`,
                            class: "article-toc-layout-panel__link",
                            onClick: onMobileLinkClick
                          }, toDisplayString(item.text), 9, _hoisted_13)
                        ], 2);
                      }), 128))
                    ])
                  ])
                ])
              ])
            ])
          ], 34)
        ])) : createCommentVNode("", true),
        isDesktop.value && tocItems.value.length ? (openBlock(), createElementBlock("div", _hoisted_14, [
          createElementVNode("aside", {
            ref_key: "boxRef",
            ref: boxRef,
            class: "article-toc-layout-panel__box",
            "aria-label": _ctx.asideAriaLabel
          }, [
            createVNode(_sfc_main$2, {
              tag: "button",
              "style-type": "secondary",
              class: "article-toc-layout-panel__skip-btn visually-hidden-focusable",
              onClick: onSkipBtnClick
            }, {
              default: withCtx(() => [
                createTextVNode(toDisplayString(_ctx.tocSkipBtnText), 1)
              ]),
              _: 1
            }),
            createElementVNode("nav", _hoisted_16, [
              createElementVNode("h2", _hoisted_17, toDisplayString(_ctx.navHeading), 1),
              createElementVNode("ol", _hoisted_18, [
                (openBlock(true), createElementBlock(Fragment, null, renderList(tocItems.value, (item) => {
                  return openBlock(), createElementBlock("li", {
                    key: item.id,
                    class: normalizeClass({ "is-active": item.isActive })
                  }, [
                    createElementVNode("a", {
                      href: `#${item.id}`,
                      class: "article-toc-layout-panel__link"
                    }, toDisplayString(item.text), 9, _hoisted_19)
                  ], 2);
                }), 128))
              ])
            ])
          ], 8, _hoisted_15)
        ])) : createCommentVNode("", true),
        createElementVNode("div", _hoisted_20, [
          renderSlot(_ctx.$slots, "default")
        ], 512)
      ], 4);
    };
  }
});

export { _sfc_main as default };
